import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Clock, Gauge, Globe2, Palmtree, UserPlus, Users, Activity, KeyRound } from 'lucide-react';
import { pctTone } from '@/components/ProductivityParts';
import { type DailyReportDto, type PeriodDto, type ProductivitySummaryDto, type EmployeeDetail, type LeaveBalanceDto, type LeaveRequestDto, type WorkScheduleDto, type WorkTermsDto } from '@workforce/shared';
import { formatMinutes, periodName, t } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { country, flag, fmtDate } from '@/lib/format';
import { Avatar, Badge, Card, EmptyState, ErrorBanner, PageHeader, Spinner, StatCard } from '@/components/ui';
import { fmtRange, statusTone as leaveTone } from '@/lib/leave';
import { DateTime } from 'luxon';
import { WeekView } from './EmployeeDetail';
import { ClockCard } from './MyAttendance';
import { PeriodBadge, periodRange } from './Reports';

interface Overview {
  scope: 'company' | 'team';
  employees: { total: number; probation: number; countries: number; withoutLogin: number };
  byCountry: { countryCode: string; count: number }[];
  byDepartment: { name: string; count: number }[];
  recentHires: { id: string; fullName: string; position: string | null; startDate: string; countryCode: string }[];
  leave: {
    today: string;
    pendingRequests: number;
    onLeaveToday: number;
    thisWeek: { requestId: string; employeeId: string; employeeName: string; leaveType: string; color: string; startDate: string; endDate: string }[];
  };
  reportingPeriod: { current: PeriodDto; previous: PeriodDto | null };
  attendance: {
    working: number;
    absent: number;
    late: number;
    missingYesterday: number;
    lateList: { employeeId: string; name: string; minutes: number }[];
    missingList: { employeeId: string; name: string; date: string }[];
  };
}

export function DashboardPage() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === 'employee') return <EmployeeDashboard />;
  return <ManagementDashboard />;
}

function Bar({ label, value, max }: { label: string; value: number; max: number }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-40 truncate text-slate-600">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-brand-500" style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
      </div>
      <span className="w-8 text-end font-medium tabular-nums text-slate-800">{value}</span>
    </div>
  );
}

function ManagementDashboard() {
  const { user, can } = useAuth();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<Overview>('/dashboard/overview') });
  if (q.isLoading) return <Spinner />;
  if (!q.data) return <ErrorBanner error={q.error} />;
  const d = q.data;
  const team = d.scope === 'team';
  const maxC = Math.max(...d.byCountry.map((c) => c.count), 0);
  const maxD = Math.max(...d.byDepartment.map((c) => c.count), 0);
  return (
    <div>
      <PageHeader
        title={t('Hello, {name}', { name: user?.employee?.fullName.split(' ')[0] ?? t('Administrator') })}
        subtitle={team ? t('Your team at a glance') : t('{company} at a glance', { company: user?.companyName })}
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label={team ? t('Team members') : t('Total employees')} value={d.employees.total} icon={<Users className="h-4 w-4" />} tone="brand" />
        <Link to="/attendance" className="block">
          <StatCard label={t('Currently working')} value={d.attendance.working} hint={d.attendance.late ? t('{n} arrived late', { n: d.attendance.late }) : undefined} icon={<Activity className="h-4 w-4" />} tone="green" />
        </Link>
        <StatCard label={t('On leave today')} value={d.leave.onLeaveToday} icon={<Palmtree className="h-4 w-4" />} tone="amber" />
        <Link to="/attendance" className="block">
          <StatCard label={t('Absent / not in yet')} value={d.attendance.absent} hint={t('Past their start time, no punch, not on leave')} icon={<AlertTriangle className="h-4 w-4" />} tone={d.attendance.absent ? 'red' : 'slate'} />
        </Link>
        <Link to="/leave" className="block">
          <StatCard
            label={t('Pending leave requests')}
            value={d.leave.pendingRequests}
            hint={d.leave.pendingRequests ? t('Review →') : t('All caught up')}
            icon={<CalendarClock className="h-4 w-4" />}
            tone={d.leave.pendingRequests ? 'brand' : 'slate'}
          />
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card title={t('Employees by country')} actions={<Globe2 className="h-4 w-4 text-slate-400" />}>
          {d.byCountry.length ? (
            <div className="space-y-2.5">
              {d.byCountry.map((c) => (
                <Bar key={c.countryCode} label={`${flag(c.countryCode)} ${country(c.countryCode)}`} value={c.count} max={maxC} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t('No employees yet.')}</p>
          )}
        </Card>
        <Card title={t('Employees by department')}>
          {d.byDepartment.length ? (
            <div className="space-y-2.5">
              {d.byDepartment.map((c) => (
                <Bar key={c.name} label={c.name} value={c.count} max={maxD} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t('No employees yet.')}</p>
          )}
        </Card>
        <Card title={t('Recent hires')} actions={<UserPlus className="h-4 w-4 text-slate-400" />} bodyClass="p-2">
          {d.recentHires.length ? (
            <ul>
              {d.recentHires.map((h) => (
                <li key={h.id}>
                  <Link to={`/employees/${h.id}`} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-slate-50">
                    <Avatar name={h.fullName} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{h.fullName}</p>
                      <p className="truncate text-xs text-slate-500">
                        {h.position ?? '—'} · {flag(h.countryCode)}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">{fmtDate(h.startDate, 'dd LLL yy')}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={t('No employees yet')} />
          )}
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title={t('Alerts')}>
          <ul className="space-y-3 text-sm">
            {!team && d.employees.withoutLogin > 0 && (
              <li className="flex items-start gap-2">
                <KeyRound className="mt-0.5 h-4 w-4 text-amber-500" />
                <span>
                  <Link to="/employees" className="font-medium text-brand-700 hover:underline">
                    {d.employees.withoutLogin > 1 ? t('{n} employees', { n: d.employees.withoutLogin }) : t('{n} employee', { n: d.employees.withoutLogin })}
                  </Link>{' '}
                  {t('without a login account.')}
                </span>
              </li>
            )}
            {d.leave.pendingRequests > 0 && (
              <li className="flex items-start gap-2">
                <CalendarClock className="mt-0.5 h-4 w-4 text-brand-500" />
                <span>
                  <Link to="/leave" className="font-medium text-brand-700 hover:underline">
                    {d.leave.pendingRequests > 1 ? t('{n} leave requests', { n: d.leave.pendingRequests }) : t('{n} leave request', { n: d.leave.pendingRequests })}
                  </Link>{' '}
                  {t('waiting for approval.')}
                </span>
              </li>
            )}
            {d.employees.probation > 0 && (
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 text-slate-400" />
                <span>{t('{n} on probation.', { n: d.employees.probation })}</span>
              </li>
            )}
            {d.attendance.missingList.map((m) => (
              <li key={m.employeeId} className="flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 text-amber-500" />
                <span>
                  <Link to={`/attendance?tab=employee&employee=${m.employeeId}`} className="font-medium text-brand-700 hover:underline">
                    {m.name}
                  </Link>{' '}
                  {t('has a missing clock-in/out on {date}.', { date: fmtDate(m.date, 'ccc dd LLL') })}
                </span>
              </li>
            ))}
            {d.attendance.lateList.length > 0 && (
              <li className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
                <span>
                  {t('Late today: {names}', { names: d.attendance.lateList.map((l) => `${l.name} (${formatMinutes(l.minutes)})`).join(', ') })}
                </span>
              </li>
            )}
            {can('productivity.read') && <ProductivityAlerts />}
          </ul>
        </Card>
        <Card title={t("Who's off this week")} actions={<Link to="/leave" className="text-xs font-medium text-brand-700 hover:underline">{t('Calendar →')}</Link>} bodyClass="p-2">
          {d.leave.thisWeek.length ? (
            <ul>
              {d.leave.thisWeek.map((w) => (
                <li key={w.requestId} className="flex items-center gap-3 rounded-lg px-3 py-2">
                  <Avatar name={w.employeeName} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{w.employeeName}</p>
                    <p className="truncate text-xs" style={{ color: w.color }}>
                      {t(w.leaveType)}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500">
                    {w.endDate !== w.startDate
                      ? t('{start} → {end}', { start: fmtDate(w.startDate, 'dd LLL'), end: fmtDate(w.endDate, 'dd LLL') })
                      : fmtDate(w.startDate, 'dd LLL')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-4 text-sm text-slate-500">{t('Nobody is on approved leave in the next 7 days.')}</p>
          )}
        </Card>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <PeriodCard rp={d.reportingPeriod} />
        {can('productivity.read') && <ProductivityCard />}
      </div>
    </div>
  );
}

function useRecentProductivity() {
  const zone = useAuth().user?.companyTimezone ?? 'UTC';
  const yesterday = DateTime.now().setZone(zone).minus({ days: 1 });
  const week = { from: yesterday.minus({ days: 9 }).toISODate()!, to: yesterday.toISODate()! };
  return useQuery({
    queryKey: ['productivity', 'summary', week.from, week.to],
    queryFn: () => api.get<ProductivitySummaryDto>(`/productivity/summary?from=${week.from}&to=${week.to}`),
  });
}

/** Alerts from computer activity: below target on the last working day, unclassified time. */
function ProductivityAlerts() {
  const { can } = useAuth();
  const q = useRecentProductivity();
  const s = q.data;
  if (!s) return null;
  const last = [...s.daily].reverse().find((d) => d.trackedMinutes > 0)?.date;
  const below = last
    ? s.employees.filter((e) => {
        const d = e.daily.find((x) => x.date === last);
        return d && d.trackedMinutes > 0 && (d.productivityPct ?? 0) < s.targetPct;
      })
    : [];
  return (
    <>
      {below.length > 0 && (
        <li className="flex items-start gap-2">
          <Gauge className="mt-0.5 h-4 w-4 text-amber-500" />
          <span>
            {t('Below the {pct}% target on {date}:', { pct: s.targetPct, date: fmtDate(last!, 'ccc dd LLL') })}{' '}
            {below.map((e, i) => (
              <span key={e.employeeId}>
                {i > 0 && ', '}
                <Link to={`/productivity?tab=employee&employee=${e.employeeId}`} className="font-medium text-brand-700 hover:underline">{e.employeeName}</Link>
              </span>
            ))}
          </span>
        </li>
      )}
      {can('productivity.rules') && s.totals.unknownMinutes >= 60 && (
        <li className="flex items-start gap-2">
          <Gauge className="mt-0.5 h-4 w-4 text-slate-400" />
          <span>
            {t('{time} of apps and websites not classified in the last 10 days —', { time: formatMinutes(s.totals.unknownMinutes) })}{' '}
            <Link to="/productivity?tab=unclassified" className="font-medium text-brand-700 hover:underline">{t('classify them')}</Link>.
          </span>
        </li>
      )}
    </>
  );
}

/** The last finished working day's computer activity for the team, with a link to the daily report. */
function ProductivityCard() {
  const recent = useRecentProductivity();
  const week = { to: DateTime.now().setZone(useAuth().user?.companyTimezone ?? 'UTC').minus({ days: 1 }).toISODate()! };
  // Skip weekends and holidays: the most recent day with any activity.
  const date = [...(recent.data?.daily ?? [])].reverse().find((d) => d.trackedMinutes > 0)?.date ?? week.to;
  const q = useQuery({
    queryKey: ['productivity', 'daily-report', date],
    queryFn: () => api.get<DailyReportDto>(`/productivity/daily-report?date=${date}`),
    enabled: !!recent.data,
  });
  const r = q.data;
  const tracked = r?.rows.filter((x) => x.productivity && x.productivity.trackedMinutes > 0) ?? [];
  const below = tracked.filter((x) => (x.productivity!.productivityPct ?? 0) < (r?.targetPct ?? 0));
  return (
    <Card title={<span className="flex items-center gap-2"><Gauge className="h-4 w-4 text-brand-600" /> {t('Productivity — {date}', { date: DateTime.fromISO(date).toFormat('cccc d LLL') })}</span>} actions={<Link to={`/productivity?tab=report&date=${date}`} className="text-sm font-medium text-brand-700 hover:underline">{t('Daily report')}</Link>}>
      {recent.isLoading || q.isLoading ? (
        <Spinner />
      ) : !r || !tracked.length ? (
        <p className="text-sm text-slate-500">{t('No computer activity recorded in the last 10 days.')}</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-end gap-6">
            <div>
              <p className="text-xs text-slate-500">{t('Team score')}</p>
              <p className={`text-3xl font-semibold ${pctTone(r.totals.productivityPct, r.targetPct)}`}>{r.totals.productivityPct ?? '—'}%</p>
            </div>
            <dl className="grid grid-cols-3 gap-4 text-sm">
              <div><dt className="text-xs text-slate-500">{t('Productive')}</dt><dd className="font-medium">{formatMinutes(r.totals.productiveMinutes)}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('Unproductive')}</dt><dd className="font-medium">{formatMinutes(r.totals.unproductiveMinutes)}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('Idle')}</dt><dd className="font-medium">{formatMinutes(r.totals.idleMinutes)}</dd></div>
            </dl>
          </div>
          <p className="text-sm text-slate-600">
            {tracked.length === 1 ? t('{n} person tracked.', { n: tracked.length }) : t('{n} people tracked.', { n: tracked.length })}{' '}
            {below.length ? t('Below the {pct}% target: {names}.', { pct: r.targetPct, names: below.map((b) => b.employeeName).join(', ') }) : t('Everyone at or above the {pct}% target.', { pct: r.targetPct })}
          </p>
        </div>
      )}
    </Card>
  );
}

function PeriodCard({ rp }: { rp: Overview['reportingPeriod'] }) {
  const { current, previous } = rp;
  const today = DateTime.now().toISODate()!;
  const total = DateTime.fromISO(current.endDate).diff(DateTime.fromISO(current.startDate), 'days').days + 1;
  const done = Math.min(total, Math.max(0, DateTime.fromISO(today).diff(DateTime.fromISO(current.startDate), 'days').days + 1));
  const left = Math.max(0, Math.round(total - done));
  return (
    <Card title={t('Current reporting period')} actions={<Link to={`/reports?period=${current.id}`} className="text-xs font-medium text-brand-700 hover:underline">{t('Report →')}</Link>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-lg font-semibold text-slate-900">{periodName(current.label)}</p>
          <p className="text-sm text-slate-500">{periodRange(current)} · {left === 1 ? t('{n} day left', { n: left }) : t('{n} days left', { n: left })}</p>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-brand-500" style={{ width: `${(done / total) * 100}%` }} />
          </div>
        </div>
        {previous && (
          <div className="rounded-lg border border-slate-100 p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-slate-800">{periodName(previous.label)}</span>
              <PeriodBadge p={previous} />
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {previous.status === 'closed' ? t('Closed by {name} — figures are frozen.', { name: previous.closedBy ?? t('system') }) : t('Ended — review missing punches and pending leave, then close it.')}
            </p>
            {previous.status !== 'closed' && (
              <Link to={`/reports?period=${previous.id}`} className="mt-2 inline-block text-xs font-medium text-brand-700 hover:underline">
                {t('Review and close →')}
              </Link>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function EmployeeDashboard() {
  const { user } = useAuth();
  const profile = useQuery({ queryKey: ['me', 'profile'], queryFn: () => api.get<EmployeeDetail>('/me/profile') });
  const balances = useQuery({ queryKey: ['me', 'leave', 'balances'], queryFn: () => api.get<LeaveBalanceDto[]>('/me/leave/balances') });
  const requests = useQuery({ queryKey: ['me', 'leave', 'requests'], queryFn: () => api.get<LeaveRequestDto[]>('/me/leave/requests') });
  const primary = balances.data?.find((b) => b.isPrimary) ?? balances.data?.find((b) => b.deductsBalance);
  const today = DateTime.now().toISODate()!;
  const pendingCount = requests.data?.filter((r) => r.status === 'pending').length ?? 0;
  const upcoming = (requests.data ?? []).filter((r) => (r.status === 'approved' || r.status === 'pending') && r.endDate >= today).sort((a, b) => a.startDate.localeCompare(b.startDate));
  const schedule = useQuery({
    queryKey: ['me', 'schedule'],
    queryFn: () => api.get<{ terms: WorkTermsDto | null; schedule: WorkScheduleDto | null; upcoming: WorkTermsDto[] }>('/me/schedule'),
  });
  return (
    <div>
      <PageHeader title={t('Hello, {name}', { name: user?.employee?.fullName.split(' ')[0] ?? '' })} subtitle={profile.data?.position ?? undefined} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Link to="/me/balance" className="block">
          <StatCard
            label={t('Vacation balance')}
            value={primary ? t('{n} days', { n: primary.remaining }) : '—'}
            hint={primary ? t('of {n} this leave year', { n: primary.accrued + primary.carriedOver + primary.adjusted }) : undefined}
            icon={<Palmtree className="h-4 w-4" />}
            tone="green"
          />
        </Link>
        <Link to="/me/leave" className="block">
          <StatCard label={t('Pending requests')} value={pendingCount} icon={<CalendarClock className="h-4 w-4" />} tone="amber" />
        </Link>
        <StatCard label={t('Days taken')} value={primary ? primary.used : '—'} hint={primary ? t(primary.leaveTypeName) : undefined} />
      </div>
      <div className="mt-6">
        <ClockCard />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card title={t('My work schedule')} className="lg:col-span-2">
          {schedule.isLoading ? (
            <Spinner />
          ) : schedule.data?.schedule ? (
            <>
              <p className="mb-3 text-sm text-slate-600">
                <span className="font-medium text-slate-900">{schedule.data.schedule.name}</span> ·{' '}
                {t('{time} per week · times in {tz}', { time: formatMinutes(schedule.data.schedule.weeklyMinutes), tz: schedule.data.terms?.timezone })}
              </p>
              <WeekView days={schedule.data.schedule.days} />
              {schedule.data.upcoming.length > 0 && (
                <p className="mt-3 text-xs text-violet-700">
                  {t('Upcoming change from {date}: {schedule} ({tz})', { date: fmtDate(schedule.data.upcoming[0]!.effectiveFrom), schedule: schedule.data.upcoming[0]!.workScheduleName, tz: schedule.data.upcoming[0]!.timezone })}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">{t('No schedule assigned yet.')}</p>
          )}
        </Card>
        <Card title={t('My details')}>
          {profile.data && (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-2"><dt className="text-slate-500">{t('Employee ID')}</dt><dd>{profile.data.employeeCode}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">{t('Department')}</dt><dd>{profile.data.departmentName ?? '—'}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">{t('Manager')}</dt><dd>{profile.data.managerName ?? '—'}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">{t('Location')}</dt><dd>{flag(profile.data.countryCode)} {profile.data.city ?? country(profile.data.countryCode)}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">{t('Holidays')}</dt><dd>{profile.data.currentTerms?.holidayCalendarName ?? '—'}</dd></div>
            </dl>
          )}
          <Link to="/profile" className="mt-4 inline-block text-sm font-medium text-brand-700 hover:underline">
            {t('View full profile →')}
          </Link>
        </Card>
        <Card title={t('My leave')} actions={<Link to="/me/leave" className="text-xs font-medium text-brand-700 hover:underline">{t('Request leave →')}</Link>} className="lg:col-span-3">
          {upcoming.length ? (
            <ul className="divide-y divide-slate-100">
              {upcoming.slice(0, 5).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                    {t(r.leaveTypeName)} · {fmtRange(r)}
                  </span>
                  <Badge tone={leaveTone(r.status)}>{r.status === 'pending' ? t('Pending') : t('Approved')}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">{t('No upcoming leave.')}</p>
          )}
        </Card>
      </div>
    </div>
  );
}
